// package imports
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Cookies from 'universal-cookie';

// CSS imports
import './LoginModal.css';

// Component imports
import Modal from '../../modal/Modal';
import Login from '../login/Login';
import EmailLogin from '../emailLogin/EmailLogin';
import Signup from '../signup/Signup';
import EmailSignup from '../emailSignup/EmailSignup';
import Alert from '../../alert/Alert';

// Service imports
import { ApiService } from '../../../services/ApiService';

// Icon imports
import { MdError } from 'react-icons/md';

// Type imports
import { ModalSize } from '../../../types/modal/ModalSize';
import { LoginPage } from '../../../types/login/LoginPage';
import { User } from '../../../types/user/User';
import { AlertSize } from '../../../types/alert/AlertSize';
import SignupHint from '../signupHint/SignupHint';

type Props = {
  showLogin: boolean;
  googleAuthDone: boolean;
  setShowLogin: (x: boolean) => void;
  setGoogleAuthDone: (x: boolean) => void;
  setUser: (x: User | null) => void;
};

const LoginModal: React.FC<Props> = ({
  googleAuthDone,
  showLogin,
  setGoogleAuthDone,
  setShowLogin,
  setUser,
}: Props) => {
  const [page, setPage] = useState(LoginPage.LOGIN);
  const [isLoading, setIsLoading] = useState(false);
  const [showUnknownError, setShowUnknownError] = useState(false);

  const [searchParams] = useSearchParams();

  const navigate = useNavigate();

  const switchPage = (newPage: LoginPage) => {
    setPage(newPage);
  };

  const finishGoogleAuth = useCallback(
    async (code: string) => {
      setIsLoading(true);

      const cookies = new Cookies();
      const pathname = cookies.get('googleAuthPathname');
      const previousPage = cookies.get('googleAuthTriggerPage');

      if (pathname) {
        navigate(pathname);
      }

      if (previousPage && previousPage === 'signup') {
        switchPage(LoginPage.SIGNUP);
      }

      const res: User | boolean | null = await ApiService.googleAuth(
        previousPage && previousPage === 'signup' ? 'signup' : 'login',
        code
      );

      setIsLoading(false);

      if (res) {
        if (typeof res !== 'boolean') {
          setUser(res);
          setShowLogin(false);
        } else {
          switchPage(LoginPage.SIGNUPHINT);
        }
      } else {
        setShowUnknownError(true);
      }
    },
    [navigate, setShowLogin, setUser]
  );

  useEffect(() => {
    const code = searchParams.get('code');
    if (code && !googleAuthDone) {
      setShowLogin(true);
      setGoogleAuthDone(true);
      setTimeout(() => {
        finishGoogleAuth(code);
      }, 1);
    }
  }, [
    searchParams,
    googleAuthDone,
    setShowLogin,
    setGoogleAuthDone,
    finishGoogleAuth,
  ]);

  useEffect(() => {
    if (!showLogin) {
      // Remove Checkout Cookie
      const cookies = new Cookies();
      cookies.remove('checkoutTriggered', { path: '/' });

      setPage(LoginPage.LOGIN);
      setIsLoading(false);
    }
  }, [showLogin]);

  return (
    <Modal
      showModal={showLogin}
      setShowModal={setShowLogin}
      size={ModalSize.SMALL}
      isLoading={isLoading}
    >
      {page === LoginPage.LOGIN ? (
        <Login switchPage={switchPage} setIsLoading={setIsLoading} />
      ) : page === LoginPage.EMAILLOGIN ? (
        <EmailLogin
          setUser={setUser}
          switchPage={switchPage}
          setIsLoading={setIsLoading}
          setShowLogin={setShowLogin}
        />
      ) : page === LoginPage.SIGNUP ? (
        <Signup switchPage={switchPage} setIsLoading={setIsLoading} />
      ) : page === LoginPage.EMAILSIGNUP ? (
        <EmailSignup switchPage={switchPage} setIsLoading={setIsLoading} />
      ) : (
        <SignupHint />
      )}
      <Alert
        showAlert={showUnknownError}
        size={AlertSize.SMALL}
        time={3000}
        setShowAlert={setShowUnknownError}
      >
        <MdError size={20} />
        <span>Something went wrong!</span>
      </Alert>
    </Modal>
  );
};

export default LoginModal;
